import React, { useCallback, useEffect } from 'react';
import {
  Box,
  TextField,
  Autocomplete,
  Button,
  Stack,
  MenuItem,
  Typography,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import AddIcon from '@mui/icons-material/Add';
import RepositorySelector from '../parts/RepositorySelector';

const TypeSpecimenSection = ({
  form,
  handleChange,
  errors,
  typeCategories,
  onShowAddTypeCategory,
  countriesList,
  RanksNoLocalityConst,
  imgRows,
  handleImgRowChange,
  addImgRow,
  removeImgRow,
  urlRegex,
}) => {
  const isLocalityDisabled =
    form.current_rank &&
    RanksNoLocalityConst.includes(form.current_rank.toLowerCase());

  const countryOptions = (countriesList || []).map((c) => {
    if (typeof c === "string") return { id: c, name: c, alt_names: [], iso_a3: "" };
    const altNames = Array.isArray(c?.alt_names)
      ? c.alt_names
      : c?.alt_names
        ? [c.alt_names].flat().filter(Boolean)
        : [];
    return { id: c.id || c.iso_a3 || c.name, name: c.name || c.id || "", alt_names: altNames, iso_a3: c.iso_a3 || "" };
  });
  const selectedCountry =
    countryOptions.find(
      (c) => c.id === form.type_locality || c.name === form.type_locality
    ) || null;
  const countryFilter = createFilterOptions({
    stringify: (option) =>
      [option?.name, option?.id, option?.iso_a3, ...(option?.alt_names || [])]
        .filter(Boolean)
        .join(" "),
  });

  // 既存データが国名テキストの場合、IDに正規化する
  useEffect(() => {
    if (selectedCountry && form.type_locality !== selectedCountry.id) {
      handleChange("type_locality", selectedCountry.id);
    }
  }, [selectedCountry, form.type_locality, handleChange]);

  const typeCategoryOptions = (typeCategories || []).map((t) => ({
    id: t.id ?? "",
    label: t.description || t.name || t.id || "",
  })).filter((t) => t.id);
  const selectedTypeCategory = typeCategoryOptions.find((o) => o.id === form.type_category) || null;
  console.log("[TypeSpecimenSection] typeCategories", typeCategoryOptions.length, typeCategoryOptions.slice(0,3));

  // ★★★ 無限ループを防ぐための最も重要な修正 ★★★
  // RepositorySelectorに渡すonChange関数をuseCallbackでラップし、不要な再生成を防ぎます。
  const handleRepositoryChange = useCallback((newValue) => {
    // 親から渡された安定化済みのhandleChangeを呼び出します
    handleChange('type_repository_id', newValue);
  }, [handleChange]);

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ color: 'teal' }}>
        Type Specimen Information (Primarily for species-group names)
      </Typography>

      {/* Type Category */}
      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 1 }}>
        <Autocomplete
          sx={{ flexGrow: 1 }}
          options={typeCategoryOptions}
          value={selectedTypeCategory}
          getOptionLabel={(o) => o?.label || ""}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          onChange={(e, v) => handleChange('type_category', v?.id || '')}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Type Category (e.g., Holotype, Lectotype)"
              margin="dense"
              fullWidth
            />
          )}
        />
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={onShowAddTypeCategory}
          sx={{ mt: '15px' }}
        >
          + Add
        </Button>
      </Stack>

      {/* Type Sex */}
      <TextField
        select
        label="Type Sex"
        value={form.type_sex || ''}
        onChange={(e) => handleChange('type_sex', e.target.value || null)}
        fullWidth
        margin="dense"
      >
        <MenuItem value="">(Unknown/Not Applicable)</MenuItem>
        <MenuItem value="Male">Male</MenuItem>
        <MenuItem value="Female">Female</MenuItem>
      </TextField>

      {/* Type Locality */}
      <Autocomplete
        options={countryOptions}
        getOptionLabel={(c) => c?.name || ""}
        value={selectedCountry}
        onChange={(e, val) => handleChange("type_locality", val ? val.id : "")}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        filterOptions={countryFilter}
        disabled={isLocalityDisabled}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Type Locality (Country)"
            margin="dense"
            fullWidth
            disabled={isLocalityDisabled}
            helperText={
              isLocalityDisabled
                ? "Not applicable for this rank"
                : "Select from countries (ID). 既存データが国名の場合は選択でIDに変換されます。"
            }
          />
        )}
        sx={{ mb: 2, mt: 1 }}
      />

      {/* Type Repository */}
      <RepositorySelector
        value={form.type_repository_id || null}
        onChange={handleRepositoryChange}
      />

      {/* Type Image URLs */}
      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, color: 'text.secondary' }}>
        Type Image URLs (Optional)
      </Typography>
      {imgRows.map((row, idx) => (
        <Stack direction="row" spacing={1} key={idx} sx={{ mb: 1 }}>
          <TextField
            label={`Image ${idx + 1} Title`}
            value={row.title}
            onChange={(e) => handleImgRowChange(idx, 'title', e.target.value)}
            fullWidth
            size="small"
            error={ Boolean((row.url.trim() || row.title.trim()) && !row.title.trim()) }
            helperText={
              (row.url.trim() || row.title.trim()) && !row.title.trim()
                ? 'Title required if URL is present'
                : ''
            }
          />
          <TextField
            label={`Image ${idx + 1} URL`}
            value={row.url}
            onChange={(e) => handleImgRowChange(idx, 'url', e.target.value)}
            fullWidth
            size="small"
            error={ Boolean((row.url.trim() || row.title.trim()) && !urlRegex.test(row.url)) }
            helperText={
              (row.url.trim() || row.title.trim()) && !urlRegex.test(row.url)
                ? 'Valid URL required (http/https)'
                : ''
            }
          />
          <Button
            variant="outlined"
            color="error"
            onClick={() => removeImgRow(idx)}
            disabled={imgRows.length === 1 && !row.title.trim() && !row.url.trim()}
            sx={{ minWidth: '40px', height: '40px', alignSelf: 'center' }}
          >
            −
          </Button>
        </Stack>
      ))}

      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={addImgRow}
        sx={{ mb: 2, mt: 1 }}
      >
        + Add Image URL
      </Button>

      {errors.typeImg && (
        <Typography variant="caption" color="error" display="block">
          For rows with content, both Title and a valid URL are required.
        </Typography>
      )}
    </Box>
  );
};

export default TypeSpecimenSection;
